import { taskDate, dueLabel } from "./time";
import { PriorityIcon, TaskCheck, type TaskStatus } from "../../ui/Priority";
import { CalendarDays, CircleDashed, FolderKanban, Link2, Shapes, UserRound, UsersRound, X } from "lucide-react";
import { PeoplePicker } from "../../ui/PeoplePicker";
import "../../ui/sheet.css";
import { WorkSyncPanel } from "./WorkSyncPanel";
import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import {
  getTask,
  addComment,
  listComments,
  listActivity,
  type TaskPatch,
} from "../../api/hub/work";
import { BUSINESS_DOMAINS, type Task, type Person } from "../../api/hub/types";
import {
  useTasks,
  useWorkDirectory,
  priorityLabels,
  statusLabels,
} from "./useTasks";
import { QuickTaskInput } from "./QuickTaskInput";
import { EvidenceLinks, ownerLabel, TaskFlags, sourceLabel } from "./TaskMeta";
import "./tasks.css";
import { useWorkspaceView, useWorkspaceRecords } from '../workspace/useWorkspace';
import { WorkspaceViewControls, PageAssistant, OperationStatus } from '../workspace/WorkspaceControls';
import { ActiveFilters, FilterPopover, FilterSelect, FilterToggle, SearchField, Toolbar, type ActiveFilter } from '../../ui/Toolbar';
type RetainedTaskDraft = {
  draft: Partial<Task>;
  baseline: Task | null;
  error: string;
  conflict: boolean;
};
export function TasksPage() {
  const work = useTasks(),
    directory = useWorkDirectory(),
    [params, setParams] = useSearchParams();
  const [retainedDrafts, setRetainedDrafts] = useState<
    Record<string, RetainedTaskDraft>
  >({});
  const view = useWorkspaceView('tasks');
  const filters = view.draft.filters;
  const setFilter = (name: string, value: unknown) => { const next = { ...filters }; if (value === '' || value === undefined) delete next[name]; else next[name] = value; view.patch({ filters: next }); };
  const group = view.draft.groupBy === null ? 'none' : view.draft.groupBy === 'projectId' ? 'project' : view.draft.groupBy === 'priority' ? 'priority' : view.draft.groupBy === 'status' ? 'status' : view.draft.groupBy === 'domain' ? 'domain' : view.draft.groupBy === 'assigneeType' ? 'assigneeType' : 'person';
  const aiOnly = filters.assigneeType === 'agent';
  const [selected, setSelected] = useState<string[]>([]),
    [quick, setQuick] = useState(false),
    [error, setError] = useState("");
  const people = directory.people.data ?? [],
    projects = directory.projects.data ?? [],
    all = work.tasks.data ?? [];
  const active = all.find((t) => t.id === params.get("task"));
  const timeZone = String(directory.settings.data?.timeZone || "Asia/Shanghai");
  const records = useWorkspaceRecords<Task>({ source: 'tasks', viewId: view.activeViewId || undefined, filters: view.draft.filters, sorts: view.draft.sorts, groupBy: view.draft.groupBy, layout: view.draft.layout, timeZone });
  const tasks = records.data?.items ?? [];
  const close = () => setParams({});
  useEffect(() => {
    if (params.get("create") === "1") setQuick(true);
  }, [params]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        (e.target instanceof HTMLElement &&
          (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) ||
            e.target.isContentEditable))
      )
        return;
      if (e.key.toLowerCase() === "c") {
        e.preventDefault();
        setQuick(true);
        document
          .querySelector<HTMLInputElement>('[aria-label="一句话记任务"]')
          ?.focus();
      }
      if (active) {
        const field = (
          {
            a: "assigneeId",
            p: "priority",
            d: "dueDate",
            s: "status",
          } as Record<string, string>
        )[e.key.toLowerCase()];
        if (field) {
          e.preventDefault();
          document
            .querySelector<HTMLElement>(`.work-sheet [name="${field}"]`)
            ?.focus();
        }
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [active]);
  useEffect(() => {
    const field = params.get("field");
    if (
      active &&
      field &&
      [
        "assigneeId",
        "priority",
        "dueDate",
        "status",
        "domain",
        "projectId",
      ].includes(field)
    )
      document
        .querySelector<HTMLElement>(`.work-sheet [name="${field}"]`)
        ?.focus();
  }, [active?.id, params]);
  const apply = async (patch: Partial<Task>) => {
    try {
      const results = await work.batch.mutateAsync({ ids: selected, patch });
      const failed = results.filter((r) => !r.success);
      setSelected(failed.map((r) => r.id));
      setError(
        failed.length
          ? `${failed.length} 项未修改：${failed.map((r) => `${all.find((t) => t.id === r.id)?.identifier ?? r.id}：${r.error}`).join("；")}`
          : "",
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const labels: Record<string, string> =
    group === "project"
      ? Object.fromEntries(projects.map((p) => [p.id, p.title]))
      : group === "priority"
        ? Object.fromEntries(priorityLabels.map((p, i) => [String(i), p]))
        : Object.fromEntries(people.map((p) => [p.id, p.name]));
  const groupKey = (t: Task) =>
    group === 'status' || group === 'none' ? '全部任务' : group === 'domain' ? t.domain : group === 'assigneeType' ? (t.assigneeType === 'agent' ? 'AI' : '人') : group === "project"
      ? (t.projectId ?? "未归项目")
      : group === "priority"
        ? String(t.priority)
        : (t.assigneeId ?? "未指派");
  const groups = [...new Set(tasks.map(groupKey))];
  const move = (id: string, status: Task["status"]) => {
    const task = all.find((t) => t.id === id);
    if (task && task.status !== status)
      work.update.mutate(
        { id, patch: { status, expectedUpdatedAt: task.updatedAt, ...(task.version ? { expectedVersion: task.version } : {}) } },
        { onError: (e) => setError(e.message) },
      );
  };
  const urgentCount = tasks.filter((t) => t.priority === 1 && !["done", "canceled"].includes(t.status)).length;
  type FilterDef = { k: "person" | "priority" | "status" | "project" | "domain" | "due"; label: string; allLabel: string; options: string[][]; wide?: boolean };
  const filterDefs: FilterDef[] = [
    { k: "person", label: "负责人", allLabel: "所有人", options: people.map((p) => [p.id, p.name]) },
    { k: "status", label: "状态", allLabel: "全部", options: Object.entries(statusLabels) },
    { k: "due", label: "截止日", allLabel: "全部", options: [["today", "今天"], ["overdue", "已过期"], ["future", "今天以后"], ["none", "无截止日"]] },
    { k: "priority", label: "优先级", allLabel: "全部", options: priorityLabels.map((p, i) => [String(i), p]) },
    { k: "project", label: "项目", allLabel: "全部", options: projects.map((p) => [p.id, p.title]), wide: true },
    { k: "domain", label: "业务域", allLabel: "全部", options: BUSINESS_DOMAINS.map((d) => [d, d]) },
  ];
  // 常用的三项一直在工具栏上，其余收进「更多筛选」
  const quickFilters = filterDefs.slice(0, 3), moreFilters = filterDefs.slice(3);
  const filterKey = (k: FilterDef["k"]) => (k === "person" ? "assigneeId" : k === "project" ? "projectId" : k);
  const filterValue = (k: FilterDef["k"]) => String(k === "due" && filters.overdue ? "overdue" : filters[filterKey(k)] ?? "");
  const setFilterOf = (k: FilterDef["k"], value: string) => {
    if (k === "due") { const next = { ...filters }; delete next.due; delete next.overdue; if (value === "overdue") next.overdue = true; else if (value) next.due = value; view.patch({ filters: next }); }
    else setFilter(filterKey(k), k === "priority" && value !== "" ? Number(value) : value);
  };
  const renderFilter = (d: FilterDef, stacked = false) => {
    const raw = filters[filterKey(d.k)];
    return (
      <FilterSelect key={d.k} label={d.label} ariaLabel={`筛选${d.label}`} allLabel={d.allLabel} wide={d.wide && !stacked} value={filterValue(d.k)} onChange={(v) => setFilterOf(d.k, v)}>
        {Array.isArray(raw) && <option value={String(raw)}>{String(raw)}（多个）</option>}
        {d.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </FilterSelect>
    );
  };
  const optionText = (d: FilterDef) => { const v = filterValue(d.k); return d.options.find(([id]) => id === v)?.[1] ?? (Array.isArray(filters[filterKey(d.k)]) ? `${(filters[filterKey(d.k)] as unknown[]).length} 个` : v); };
  const moreCount = moreFilters.filter((d) => filterValue(d.k) !== "").length + (aiOnly ? 1 : 0);
  const activeFilters: ActiveFilter[] = [
    ...(filters.q ? [{ key: "q", label: `搜索：${String(filters.q)}`, onRemove: () => setFilter("q", undefined) }] : []),
    ...filterDefs.filter((d) => filterValue(d.k) !== "").map((d) => ({ key: d.k, label: `${d.label}：${optionText(d)}`, onRemove: () => setFilterOf(d.k, "") })),
    ...(aiOnly ? [{ key: "ai", label: "只看 AI 负责的", onRemove: () => setFilter("assigneeType", undefined) }] : []),
  ];
  return (
    <section className="work-page" data-layout={view.draft.layout}>
      <WorkSyncPanel />
      <header className="work-heading">
        <h1>看板</h1>
        <p className="work-stats"><span><b>{tasks.length}</b> 件</span>{urgentCount > 0 && <span data-tone="bad"><b>{urgentCount}</b> 紧急</span>}</p>
        <div className="work-heading-actions">
          <WorkspaceViewControls view={view} showGroup={false} />
          <PageAssistant context={{ source: 'tasks', viewId: view.activeViewId || undefined, selectedRecordIds: selected, timeZone }} />
          <button className="work-primary" onClick={() => setQuick((q) => !q)}>
            记任务 <kbd>C</kbd>
          </button>
        </div>
      </header>
      {quick && <QuickTaskInput autoFocus />}
      <Toolbar
        label="筛选任务"
        end={
          <FilterSelect neutral label="分组" ariaLabel="看板分组" allLabel="不分组" value={group === 'none' ? '' : group} onChange={(v) => view.patch({ groupBy: v === '' ? null : ({ person: 'assigneeId', project: 'projectId' } as Record<string, string>)[v] ?? v })}>
            <option value="person">按人</option>
            <option value="project">按项目</option>
            <option value="priority">按优先级</option>
            <option value="status">按状态</option>
            <option value="domain">按业务域</option>
            <option value="assigneeType">按负责人类型</option>
          </FilterSelect>
        }
      >
        <SearchField label="搜索任务" placeholder="搜标题或编号" value={String(filters.q ?? '')} onChange={(v) => setFilter('q', v)} />
        {quickFilters.map((d) => renderFilter(d))}
        <FilterPopover count={moreCount}>
          {moreFilters.map((d) => renderFilter(d, true))}
          <FilterToggle label="只看 AI 负责的" checked={aiOnly} onChange={(v) => setFilter('assigneeType', v ? 'agent' : undefined)} />
        </FilterPopover>
      </Toolbar>
      <ActiveFilters items={activeFilters} onClear={() => view.patch({ filters: {} })} />
      <OperationStatus receipt={work.receipt ?? null} />
      {records.error && <p role="alert">{records.error.message}</p>}
      {!!selected.length && (
        <div className="work-toolbar work-batch" aria-label="批量修改">
          <strong>已选 {selected.length} 件</strong>
          <select
            aria-label="批量状态"
            value=""
            onChange={(e) =>
              void apply({ status: e.target.value as Task["status"] })
            }
          >
            <option value="">设置状态…</option>
            {Object.entries(statusLabels).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <select
            aria-label="批量指派"
            value=""
            onChange={(e) =>
              void apply({ assigneeId: e.target.value, assigneeType: "person" })
            }
          >
            <option value="">指派给…</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            aria-label="批量优先级"
            value=""
            onChange={(e) =>
              void apply({
                priority: Number(e.target.value) as Task["priority"],
              })
            }
          >
            <option value="">设置优先级…</option>
            {priorityLabels.map((p, i) => (
              <option key={i} value={i}>
                {p}
              </option>
            ))}
          </select>
          <input
            type="date"
            aria-label="批量截止日"
            onChange={(e) => void apply({ dueDate: e.target.value || null })}
          />
          <button onClick={() => setSelected([])}>取消选择</button>
        </div>
      )}
      {(error || work.tasks.isError) && (
        <p role="alert">{error || work.tasks.error?.message}</p>
      )}
      {records.isPending ? (
        <p role="status">正在加载任务…</p>
      ) : (
        <>
          {view.draft.layout === 'list' ? <div className="work-board work-list" aria-label="任务列表">{tasks.map(t => <article key={t.id} className="work-task" data-task-id={t.id}><input type="checkbox" aria-label={`选择 ${t.identifier}`} checked={selected.includes(t.id)} onChange={e => setSelected(ids => e.target.checked ? [...ids, t.id] : ids.filter(id => id !== t.id))}/><button className="work-task-open" onClick={() => setParams({ task: t.id })}><span className="work-task-title">{t.title}</span><span className="work-task-meta"><PriorityIcon priority={t.priority} label={false}/>{t.identifier} · {statusLabels[t.status]} · {ownerLabel(t, people)}</span></button></article>)}</div> : groups.map((g) => {
            const grouped = tasks.filter((t) => groupKey(t) === g);
            return (
              <section key={g} className="work-swimlane" data-group-id={g}>
                {group !== "status" && group !== "none" && (() => {
                  // 组头只留组名和件数；有紧急、过期才多一句红字
                  const open = grouped.filter((t) => !["done", "canceled"].includes(t.status));
                  const urgent = open.filter((t) => t.priority === 1).length;
                  const late = open.filter((t) => t.dueDate && taskDate(t.dueDate, timeZone) < taskDate(Date.now(), timeZone)).length;
                  return (
                    <h2>
                      {labels[g] ?? (group === "person" && g !== "未指派" ? `离职或不在当前目录（${g}）` : g)}
                      <small>{grouped.length}</small>
                      {urgent > 0 && <small data-tone="bad">{urgent} 紧急</small>}
                      {late > 0 && <small data-tone="bad">{late} 过期</small>}
                    </h2>
                  );
                })()}
                <div className="work-board work-status-board">
                  {(
                    [
                      "triage",
                      "todo",
                      "in_progress",
                      "in_review",
                      "done",
                      "canceled",
                    ] as const
                  ).map((status) => (
                    <section
                      key={status}
                      className="work-column"
                      aria-label={`${labels[g] ?? g} · ${statusLabels[status]}`}
                      data-status={status}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const id = e.dataTransfer.getData("text/lingzhi-task");
                        if (id) move(id, status);
                      }}
                    >
                      <h3>
                        {statusLabels[status]}{" "}
                        <span>
                          {grouped.filter((t) => t.status === status).length}
                        </span>
                      </h3>
                      {grouped
                        .filter((t) => t.status === status)
                        .map((t) => (
                          <article
                            key={t.id}
                            className="work-task"
                            data-task-id={t.id}
                            data-urgent={t.priority === 1}
                            draggable
                            onDragStart={(e) =>
                              e.dataTransfer.setData("text/lingzhi-task", t.id)
                            }
                          >
                            <input
                              type="checkbox"
                              aria-label={`选择 ${t.identifier}`}
                              checked={selected.includes(t.id)}
                              onChange={(e) =>
                                setSelected((s) =>
                                  e.target.checked
                                    ? [...s, t.id]
                                    : s.filter((id) => id !== t.id),
                                )
                              }
                            />
                            <button
                              className="work-task-open"
                              onClick={() => setParams({ task: t.id })}
                              aria-label={`${t.title} ${t.identifier} · ${priorityLabels[t.priority]} · ${statusLabels[t.status]} · ${ownerLabel(t, people)} · ${dueLabel(t.dueDate, taskDate(Date.now(), timeZone), timeZone)?.text ?? "无截止日"}`}
                            >
                              <span className="work-task-title">{t.title}</span>
                              <span className="work-task-meta" aria-hidden="true">
                                <PriorityIcon priority={t.priority} label={false} />
                                <span className="work-task-id">{t.identifier}</span>
                                <TaskCheck priority={t.priority} status={t.status} size="sm" />
                                <span className="work-task-owner">{ownerLabel(t, people)}</span>
                                {t.dueDate && <span className="work-due" data-tone={["done", "canceled"].includes(t.status) ? "" : dueLabel(t.dueDate, taskDate(Date.now(), timeZone), timeZone)?.tone}>{dueLabel(t.dueDate, taskDate(Date.now(), timeZone), timeZone)?.text}</span>}
                              </span>
                              <TaskFlags task={t} />
                            </button>
                          </article>
                        ))}
                    </section>
                  ))}
                </div>
              </section>
            );
          })}
          {!tasks.length && <p className="work-empty">没有符合条件的任务</p>}
        </>
      )}
      {active && (
        <TaskSheet
          key={active.id}
          task={active}
          retained={retainedDrafts[active.id]}
          onRetain={(state) =>
            setRetainedDrafts((d) => ({ ...d, [active.id]: state }))
          }
          people={people}
          projects={projects}
          timeZone={timeZone}
          onClose={close}
          onSave={async (patch) => {
            await work.update.mutateAsync({ id: active.id, patch });
            setRetainedDrafts((d) => {
              const n = { ...d };
              delete n[active.id];
              return n;
            });
            close();
          }}
        />
      )}
    </section>
  );
}
function TaskSheet({
  task,
  retained,
  onRetain,
  people,
  projects,
  timeZone,
  onClose,
  onSave,
}: {
  task: Task | null;
  retained?: RetainedTaskDraft;
  onRetain: (state: RetainedTaskDraft) => void;
  people: Person[];
  timeZone: string;
  projects: { id: string; title: string }[];
  onClose: () => void;
  onSave: (t: TaskPatch) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Partial<Task>>(
      retained?.draft ??
        task ?? {
          title: "",
          description: "",
          assigneeId: null,
          priority: 0,
          status: "todo",
          domain: BUSINESS_DOMAINS[0],
          dueDate: null,
          projectId: null,
          followerIds: [],
          sourceUrl: "",
        },
    ),
    [comment, setComment] = useState(""),
    [mentions, setMentions] = useState<string[]>([]),
    [error, setError] = useState(retained?.error ?? ""),
    [busy, setBusy] = useState(false);
  // Keep the opening version even if a background refresh replaces the task prop.
  const [baseline, setBaseline] = useState(retained?.baseline ?? task);
  const [conflict, setConflict] = useState(retained?.conflict ?? false);
  const [latest, setLatest] = useState<Task | null>(null);
  const changed = (current: Partial<Task>, original: Task) => {
    const patch: TaskPatch = {};
    for (const key of [
      "title",
      "description",
      "assigneeId",
      "assigneeType",
      "completionEvidence",
      "followerIds",
      "dueDate",
      "priority",
      "status",
      "domain",
      "projectId",
      "sourceUrl",
    ] as const) {
      const a = current[key],
        b = original[key];
      const same =
        key === "dueDate"
          ? (a ? taskDate(a as string, timeZone) : null) ===
            (b ? taskDate(b as string, timeZone) : null)
          : key === "followerIds"
            ? JSON.stringify([...((a as string[]) ?? [])].sort()) ===
              JSON.stringify([...(b as string[])].sort())
            : a === b;
      if (!same) Object.assign(patch, { [key]: a });
    }
    return patch;
  };
  const closeSheet = () => {
    if (baseline && Object.keys(changed(draft, baseline)).length || conflict || retained) onRetain({ draft, baseline, error, conflict });
    onClose();
  };
  const readLatest = async () => {
    if (!task) return;
    setBusy(true);
    try {
      setLatest(await getTask(task.id));
    } catch (e) {
      setError(`读取最新任务失败：${(e as Error).message}。草稿已保留。`);
    } finally {
      setBusy(false);
    }
  };
  const qc = useQueryClient();
  const comments = useQuery({
      queryKey: ["hub", "comments", task?.id],
      enabled: !!task,
      queryFn: () => listComments(task!.id),
    }),
    activity = useQuery({
      queryKey: ["hub", "activity", task?.id],
      enabled: !!task,
      queryFn: () => listActivity(task!.id),
    }),
    post = useMutation({
      mutationFn: () => addComment(task!.id, comment, mentions),
      onSuccess: () => {
        setComment("");
        setMentions([]);
        void qc.invalidateQueries({ queryKey: ["hub", "comments", task?.id] });
      },
      onError: (e) => setError(e.message),
    });
  const field = <K extends keyof Task>(k: K, v: Task[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (conflict)
      return setError(
        "请先读取最新版本并核对草稿，再采用最新基线。草稿已保留。",
      );

    setBusy(true);
    try {
      const {
        title,
        description,
        assigneeId,
        followerIds,
        dueDate,
        priority,
        status,
        domain,
        projectId,
        sourceUrl,
      } = draft;
      const values = {
        title,
        description,
        assigneeId,
        followerIds,
        dueDate,
        priority,
        status,
        domain,
        projectId,
        sourceUrl,
      };
      const patch: TaskPatch = {};
      const original = baseline;
      if (original) {
        Object.assign(patch, changed(draft, original));
        if (!Object.keys(patch).length) {
          onClose();
          return;
        }
        patch.expectedUpdatedAt = original.updatedAt;
        if (original.version) patch.expectedVersion = original.version;
      } else Object.assign(patch, values);
      await onSave(patch);
    } catch (e) {
      if ((e as { status?: number }).status === 409) setConflict(true);
      setError(
        `${(e as Error).message}。草稿已保留；版本冲突时请读取最新版本核对后再保存。`,
      );
      setBusy(false);
    }
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSheet();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [closeSheet]);
  useEffect(() => {
    document.querySelector<HTMLInputElement>('[name="title"]')?.focus();
  }, []);
  const formId = `task-sheet-${task?.id ?? "new"}`;
  const who = (id?: string | null) => people.find((p) => p.id === id)?.name;
  return (
    <div className="work-sheet-backdrop" onClick={closeSheet}>
      <aside
        className="work-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={task ? `任务 ${task.identifier}` : "新建任务"}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head">
          <h2>
            {task && <TaskCheck priority={draft.priority ?? 0} status={(draft.status ?? "todo") as TaskStatus} size="sm" />}
            {task?.identifier ?? "新建任务"}
          </h2>
          <span className="sheet-head-spacer" />
          <button type="button" className="sheet-close" onClick={closeSheet} aria-label="关闭任务预览" title="关闭（Esc）">
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="sheet-body">
          {!conflict && baseline && task && (task.version !== baseline.version || task.updatedAt !== baseline.updatedAt) && <p role="status">远端任务已更新，当前草稿和原版本已保留。保存前可读取最新版本核对。<button onClick={() => { setConflict(true); void readLatest(); }}>读取最新版本并核对</button></p>}
          {conflict && <section className="sheet-conflict" aria-label="任务版本冲突核对">
            <p>草稿暂存在当前任务页，关闭抽屉再打开仍保留；离开任务页或刷新后不保留。不会自动覆盖远端新版本。</p>
            <button disabled={busy} onClick={() => void readLatest()}>读取最新版本并核对草稿</button>
            {latest && <><h3>最新任务与我的改动</h3>
              <table><thead><tr><th>字段</th><th>最新任务</th><th>我的草稿</th></tr></thead><tbody>{([...new Set([...Object.keys(baseline ? changed(latest, baseline) : latest), ...Object.keys(baseline ? changed(draft, baseline) : draft)])] as (keyof Task)[]).map(key => {
                const label = ({ title: "标题", description: "描述", assigneeId: "负责人", followerIds: "关注人", priority: "优先级", status: "状态", dueDate: "截止日", domain: "业务域", projectId: "项目", sourceUrl: "来源链接" } as Partial<Record<keyof Task, string>>)[key] ?? "其他字段";
                const show = (value: unknown) => key === "priority" ? priorityLabels[value as Task["priority"]] : key === "status" ? statusLabels[value as Task["status"]] : key === "assigneeId" ? people.find(p => p.id === value)?.name ?? String(value ?? "未指派") : key === "projectId" ? projects.find(p => p.id === value)?.title ?? String(value ?? "无") : Array.isArray(value) ? value.map(id => people.find(p => p.id === id)?.name ?? id).join("、") || "无" : String(value ?? "无");
                return <tr key={key}><th>{label}</th><td>{show(latest[key])}</td><td>{show(draft[key])}{baseline && !Object.hasOwn(changed(draft, baseline), key) && "（未修改，将保留最新值）"}</td></tr>;
              })}</tbody></table>
              <p>确认后以最新任务为基线，只保留你改过的字段；同一字段有冲突时以你的草稿为准。仍须点击保存任务才会写入。</p>
              <button disabled={busy} onClick={() => { const edits = baseline ? changed(draft, baseline) : draft; setDraft({ ...latest, ...edits }); setBaseline(latest); setLatest(null); setError(""); setConflict(false); }}>采用最新基线并保留我的改动</button>
            </>}
          </section>}
        {task && (
          <section aria-label="任务依据">
            <p>
              {ownerLabel(task, people)} · 最后改动：
              {sourceLabel(task.lastChangeSource, people)}
            </p>
            <TaskFlags task={task} />
            <EvidenceLinks task={task} />
          </section>
        )}
          <form id={formId} className="work-form" onSubmit={(e) => void submit(e)}>
            <label>
              <span className="sheet-field-label">标题</span>
              <input
                name="title"
                className="sheet-title"
                placeholder="任务标题"
                value={draft.title}
                onChange={(e) => field("title", e.target.value)}
              />
            </label>
            <label>
              <span className="sheet-field-label">描述</span>
              <textarea
                className="sheet-desc"
                placeholder="补充背景、验收标准…"
                value={draft.description}
                onChange={(e) => field("description", e.target.value)}
              />
            </label>
          <label>
            完成证据（每行一个链接或提交编号）
            <textarea
              aria-label="完成证据"
              value={(draft.completionEvidence ?? [])
                .map((e) => e.reference)
                .join("\n")}
              onChange={(e) =>
                field(
                  "completionEvidence",
                  e.target.value
                    .split("\n")
                    .filter(Boolean)
                    .map((reference) => ({
                      type: reference.startsWith("http") ? "link" : "commit",
                      reference,
                    })),
                )
              }
            />
          </label>
            <div className="sheet-props">
              <label className="sheet-prop">
                <span className="sheet-prop-k"><CircleDashed aria-hidden="true" />状态 <kbd>S</kbd></span>
                <select name="status" value={draft.status} onChange={(e) => field("status", e.target.value as Task["status"])}>
                  {Object.entries(statusLabels).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><PriorityIcon priority={draft.priority ?? 0} label={false} />优先级 <kbd>P</kbd></span>
                <select name="priority" value={draft.priority} onChange={(e) => field("priority", Number(e.target.value) as Task["priority"])}>
                  {priorityLabels.map((p, i) => <option key={p} value={i}>{p}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><UserRound aria-hidden="true" />负责人 <kbd>A</kbd></span>
                <select name="assigneeId" value={draft.assigneeId ?? ""} onChange={(e) => field("assigneeId", e.target.value || null)}>
                  <option value="">未指派</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><UserRound aria-hidden="true" />执行方</span>
                <select aria-label="执行方" value={draft.assigneeType ?? "person"} onChange={(e) => field("assigneeType", e.target.value as "person" | "agent")}>
                  <option value="person">本人</option><option value="agent">负责人的 AI</option>
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><CalendarDays aria-hidden="true" />截止日 <kbd>D</kbd></span>
                <input name="dueDate" type="date" value={draft.dueDate ? taskDate(draft.dueDate, timeZone) : ""} onChange={(e) => field("dueDate", e.target.value || null)} />
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><FolderKanban aria-hidden="true" />项目</span>
                <select name="projectId" value={draft.projectId ?? ""} onChange={(e) => field("projectId", e.target.value || null)}>
                  <option value="">无项目</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Shapes aria-hidden="true" />业务域</span>
                <select name="domain" value={draft.domain} onChange={(e) => field("domain", e.target.value)}>
                  {BUSINESS_DOMAINS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </label>
              <div className="sheet-prop">
                <span className="sheet-prop-k"><UsersRound aria-hidden="true" />关注人</span>
                <PeoplePicker label="关注人" people={people} value={draft.followerIds ?? []} onChange={(ids) => field("followerIds", ids)} />
              </div>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Link2 aria-hidden="true" />来源链接</span>
                <input type="url" placeholder="粘贴链接" value={draft.sourceUrl} onChange={(e) => field("sourceUrl", e.target.value)} />
              </label>
            </div>
          </form>
          {task && (
            <>
              <section className="sheet-section" aria-label="评论">
                <h3>评论与 @</h3>
                {comments.isError && <p role="alert">{comments.error.message}</p>}
                {comments.data?.map((c) => {
                  const name = who(c.authorId) ?? "成员";
                  return (
                    <p key={c.id} className="sheet-comment">
                      <span className="sheet-avatar" aria-hidden="true">{name.slice(0, 1)}</span>
                      <span><b>{name}</b> {c.content}</span>
                    </p>
                  );
                })}
                <div className="sheet-compose">
                  <textarea aria-label="任务评论" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="写评论，选择要 @ 的同事" />
                  <div className="sheet-compose-row">
                    <PeoplePicker label="@ 人员" prefix="@" people={people} value={mentions} onChange={setMentions} />
                    <button type="button" disabled={!comment.trim() || post.isPending} onClick={() => post.mutate()}>发送评论</button>
                  </div>
                </div>
              </section>
              <section className="sheet-section" aria-label="动态">
                <h3>动态</h3>
                {activity.isError && <p role="alert">{activity.error.message}</p>}
                <ol className="sheet-timeline">
                  {activity.data?.map((a) => (
                    <li key={a.id} className="work-meta">
                      {a.description ?? a.content ?? ({ create: "创建任务", update: "更新任务", comment: "新增评论", conflict: "同步冲突", complete: "完成任务" } as Record<string, string>)[a.kind ?? a.type ?? ""] ?? "任务已更新"}
                      {a.before !== undefined && <span> · 变更前 {JSON.stringify(a.before)} → {JSON.stringify(a.after)}</span>}
                      {a.createdAt && <time>{new Date(a.createdAt).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</time>}
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}
        </div>
        <footer className="sheet-foot">
          {error && <p className="work-error" role="alert">{error}</p>}
          <button type="submit" form={formId} className="sheet-primary work-primary" disabled={busy || conflict}>
            保存任务
          </button>
        </footer>
      </aside>
    </div>
  );
}
